import { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface TabBarItem<T extends string> {
  key: T
  label: string
  badge?: number
}

interface TabBarProps<T extends string> {
  tabs: TabBarItem<T>[]
  activeTab: T
  onTabChange: (tab: T) => void
  className?: string
  idPrefix?: string
}

export function tabId(idPrefix: string, key: string): string {
  return `${idPrefix}-tab-${key}`
}

export function tabPanelId(idPrefix: string): string {
  return `${idPrefix}-panel`
}

export function TabBar<T extends string>({ tabs, activeTab, onTabChange, className, idPrefix }: TabBarProps<T>) {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const activeIndex = Math.max(0, tabs.findIndex(t => t.key === activeTab))

  const moveTo = (index: number) => {
    const target = tabs[index]
    if (!target) return
    onTabChange(target.key)
    buttonRefs.current[index]?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      moveTo(index === last ? 0 : index + 1)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      moveTo(index === 0 ? last : index - 1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      moveTo(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      moveTo(last)
    }
  }

  return (
    <div className={cn('flex shrink-0 gap-2 bg-background rounded-lg border p-2', className)} role="tablist">
      {tabs.map((t, index) => (
        <Button
          key={t.key}
          ref={el => { buttonRefs.current[index] = el }}
          variant={activeTab === t.key ? 'outline' : 'ghost'}
          size="sm"
          role="tab"
          id={idPrefix ? tabId(idPrefix, t.key) : undefined}
          aria-controls={idPrefix ? tabPanelId(idPrefix) : undefined}
          aria-selected={activeTab === t.key}
          tabIndex={index === activeIndex ? 0 : -1}
          onKeyDown={event => handleKeyDown(event, index)}
          onClick={() => onTabChange(t.key)}
          className={cn(
            'text-sm h-10 rounded-lg px-5',
            activeTab === t.key
              ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {t.label}
          {t.badge != null && t.badge > 0 && (
            <span className={cn(
              'ml-1.5 inline-flex items-center justify-center rounded-full text-xs font-bold min-w-[20px] h-5 px-1.5 pb-0.5',
              activeTab === t.key ? 'bg-primary-foreground text-brand-panel dark:text-primary' : 'bg-button text-button-foreground',
            )}>
              {t.badge}
            </span>
          )}
        </Button>
      ))}
    </div>
  )
}
